<template>
    <div
        v-click-outside="onClickOutside"
        :class="{
            '!top-12': hasNewHeader,
            'bg-secondary border-transparent shadow-none': isBao
        }"
        class="menu-sticky-header z-50 bg-secondary border-t border-gray-200 shadow-md md:border-b md:shadow-none"
    >
        <div class="mx-auto w-full md:px-5 lg:px-8">
            <div class="container-lg">
                <div class="flex flex-row">
                    <nav
                        :class="[
                            isSearchEnabled
                                ? ''
                                : 'venue-category-menu-no-search',
                            { 'visual-menu': visualMenu }
                        ]"
                        class="venue-category-menu relative flex md:pr-8"
                    >
                        <div
                            class="relative flex items-center w-full min-w-0"
                            style="flex: 0 1 100%"
                        >
                            <!-- SCROLL mode: use scrollactive for anchor-based navigation -->
                            <scrollactive
                                v-if="
                                    categories &&
                                    categories.length &&
                                    !hasCategoryRouting
                                "
                                :class="{
                                    'thin-scrollbar':
                                        $store.state.venue?.venue?.id === 9250
                                }"
                                :modifyUrl="false"
                                :offset="computedOffset"
                                active-class="sticky-item-active"
                                class="venue-category-menu__list no-scrollbar flex-nowrap select-none pl-2 text-accent sm:pl-0 md:flex"
                                v-on:itemchanged="onItemChanged"
                            >
                                <a
                                    v-for="(category, index) in navCategories"
                                    :id="'#menu-category-' + category.id"
                                    :key="index"
                                    :aria-label="
                                        'Navigate to ' +
                                        category.name +
                                        ' category'
                                    "
                                    :class="{
                                        '!my-4 !px-4 text-heading font-body !text-base tracking-tight':
                                            isBao,
                                        'min-w-36': hasCategoryHeaderImages
                                    }"
                                    :href="'#category-' + category.id"
                                    class="scrollactive-item font-button inline-block my-3 px-3 text-subheading text-sm sm:text-base font-medium rounded-full transition duration-100 sm:ml-2 sm:ml-3 md:my-3.5 md:px-3"
                                    role="menuitem"
                                    tabindex="0"
                                    @click="moveSliderElement($event.target)"
                                >
                                    <MenuStickyHeaderCategoryLabel
                                        :category="category"
                                        :hasCategoryHeaderImages="
                                            hasCategoryHeaderImages
                                        "
                                    />
                                </a>
                                <div
                                    v-show="isSliderElementVisible"
                                    :style="sliderElementProperties"
                                    class="menu-slider-element"
                                ></div>
                            </scrollactive>
                            <!-- PAGES mode: use buttons for route-based navigation -->
                            <div
                                v-else-if="
                                    categories &&
                                    categories.length &&
                                    hasCategoryRouting
                                "
                                :class="{
                                    'thin-scrollbar':
                                        $store.state.venue?.venue?.id === 9250
                                }"
                                class="venue-category-menu__list no-scrollbar flex-nowrap select-none pl-2 text-accent sm:pl-0 md:flex"
                            >
                                <button
                                    v-for="(category, index) in navCategories"
                                    :key="index"
                                    :aria-label="
                                        'Navigate to ' +
                                        category.name +
                                        ' category'
                                    "
                                    :class="{
                                        '!my-4 !px-4 text-heading font-body !text-base tracking-tight':
                                            isBao,
                                        'min-w-36': hasCategoryHeaderImages,
                                        'sticky-item-active':
                                            isActiveCategory(category)
                                    }"
                                    class="scrollactive-item font-button inline-block my-3 px-3 text-subheading text-sm sm:text-base font-medium rounded-full transition duration-100 sm:ml-2 sm:ml-3 md:my-3.5 md:px-3"
                                    type="button"
                                    @click="onCategoryNavClick(category)"
                                >
                                    <MenuStickyHeaderCategoryLabel
                                        :category="category"
                                        :hasCategoryHeaderImages="
                                            hasCategoryHeaderImages
                                        "
                                    />
                                </button>
                                <div
                                    v-show="isSliderElementVisible"
                                    :style="sliderElementProperties"
                                    class="menu-slider-element"
                                ></div>
                            </div>
                            <div
                                v-else
                                class="venue-category-menu__list no-scrollbar flex-nowrap text-blue-900 md:flex"
                            >
                                <span
                                    v-for="(n, index) in 5"
                                    :id="'loading-cat-' + n"
                                    :key="index"
                                    class="inline-block ml-3 my-2.5 px-2 w-28 rounded-full md:my-3 md:pt-1 md:px-3"
                                >
                                    <vue-content-loading
                                        :height="17"
                                        :speed="2"
                                        :width="100"
                                        primaryColor="#f3f3f3"
                                        secondaryColor="#ecebeb"
                                    >
                                        <rect
                                            height="10"
                                            rx="0"
                                            ry="0"
                                            width="93"
                                            x="0"
                                            y="3"
                                        />
                                    </vue-content-loading>
                                </span>
                            </div>
                            <span v-if="!isLoading && isSearchEnabled">
                                <div
                                    :style="
                                        isSearchExpanded
                                            ? 'width: 100%;'
                                            : 'width: 50px'
                                    "
                                    class="searchInput relative flex grow items-stretch cursor-pointer"
                                    @click="focusOnSearchInput"
                                >
                                    <div
                                        class="absolute inset-y-0 left-0 flex items-center"
                                    >
                                        <svg
                                            :class="
                                                isSearchExpanded
                                                    ? 'mx-2'
                                                    : 'ml-4'
                                            "
                                            :style="[
                                                isBao
                                                    ? 'margin-left: 0.4rem'
                                                    : ''
                                            ]"
                                            class="z-100 w-6 h-6 text-primary"
                                            fill="currentColor"
                                            viewBox="0 0 20 20"
                                            xmlns="http://www.w3.org/2000/svg"
                                        >
                                            <path
                                                clip-rule="evenodd"
                                                d="M8 4a4 4 0 100 8 4 4 0 000-8zM2 8a6 6 0 1110.89 3.476l4.817 4.817a1 1 0 01-1.414 1.414l-4.816-4.816A6 6 0 012 8z"
                                                fill-rule="evenodd"
                                            ></path>
                                        </svg>
                                    </div>
                                    <div
                                        class="absolute inset-y-0 right-0 flex items-center pl-3"
                                    >
                                        <button
                                            v-show="isSearchExpanded"
                                            aria-label="Cancel search"
                                            class="relative inline-flex items-center -ml-px px-4 py-2 text-gray-700 text-itembody text-sm font-medium hover:bg-gray-100 hover:bg-secondary rounded-sm focus:outline-none space-x-2"
                                            tabindex="0"
                                            @click.stop="cancelSearch"
                                        >
                                            <span>
                                                {{
                                                    $t(
                                                        'menuStickyHeader.cancel'
                                                    )
                                                }}
                                            </span>
                                        </button>
                                    </div>
                                    <input
                                        id="searchInput"
                                        ref="searchInput"
                                        v-model="search"
                                        :aria-label="
                                            $t('menuStickyHeader.searchTheMenu')
                                        "
                                        :placeholder="
                                            isSearchExpanded
                                                ? $t(
                                                      'menuStickyHeader.searchTheMenu'
                                                  )
                                                : ''
                                        "
                                        autocomplete="off"
                                        class="font-heading pl-10 w-full text-heading text-sm leading-5 bg-secondary border-none focus:outline-none"
                                        name="search"
                                        type="text"
                                    />
                                </div>
                            </span>
                        </div>
                        <span
                            v-if="hasAllergenFilters"
                            class="relative flex items-center justify-center p-2 sm:p-1.5 -ml-2 sm:ml-0 cursor-pointer"
                            @click="openAllergenFiltersModal"
                        >
                            <svg
                                class="w-5 h-5 text-primary"
                                fill="none"
                                stroke="currentColor"
                                viewBox="0 0 24 24"
                            >
                                <path
                                    d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2.586a1 1 0 01-.293.707l-6.414 6.414a1 1 0 00-.293.707V17l-4 4v-6.586a1 1 0 00-.293-.707L3.293 7.293A1 1 0 013 6.586V4z"
                                    stroke-linecap="round"
                                    stroke-linejoin="round"
                                    stroke-width="2"
                                ></path>
                            </svg>

                            <span
                                v-if="
                                    allergenFilters.length > 0 ||
                                    dietFilters.length > 0
                                "
                                class="absolute top-1 right-1 sm:-right-1 inline-flex items-center justify-center rounded-full bg-button text-button text-[10px] font-semibold leading-none h-4 min-w-4 px-1 ring-2 ring-white shadow"
                            >
                                {{
                                    allergenFilters.length + dietFilters.length
                                }}
                            </span>
                        </span>
                    </nav>
                    <div class="basket-wrapper relative flex">
                        <basket :showBasket="showBasket" />
                    </div>
                </div>
                <DietaryFilters
                    v-if="hasDietaryFilters"
                    v-show="isSearchExpanded"
                    :iconStyle="
                        $store.state.venue.venue.branding?.dietIconStyle
                    "
                    :showIcons="true"
                />
            </div>
        </div>
    </div>
</template>

<script>
import Basket from './Basket.vue';
import easyScroll from 'easy-scroll';
import { mapActions, mapGetters, mapState } from 'vuex';
import isMobile from '@/helpers/isMobileDevice';
import VueContentLoading from 'vue-content-loading';
import vClickOutside from 'v-click-outside';
import DietaryFilters from '@/components/DietaryFilters';
import MenuStickyHeaderCategoryLabel from '@/components/MenuStickyHeaderCategoryLabel.vue';
import {
    FulfillmentMethodsEnum,
    CategoryNavImagesEnum,
    CategoryRoutingEnum
} from '@/enums';
import * as menuService from '@/services/menu.service.js';

export default {
    name: 'MenuStickyHeader',
    components: {
        DietaryFilters,
        Basket,
        VueContentLoading,
        MenuStickyHeaderCategoryLabel
    },
    directives: {
        clickOutside: vClickOutside.directive
    },
    data() {
        return {
            sliderElementProperties: {
                left: 0,
                width: 0,
                height: 0,
                top: 0
            },
            isSliderElementVisible: false,
            search: '',
            isSearchExpandedKey: 0
        };
    },
    props: {
        showBasket: {
            type: Boolean,
            required: false,
            default: false
        },
        hasNewHeader: {
            type: Boolean,
            required: false,
            default: false
        }
    },
    created() {
        if (this.isSearchEnabled) {
            this.search = this.$route.query.search || '';
        }
    },
    mounted() {
        const BAO_VENUES = [9713, 9712, 9711, 9710, 9708, 8376, 9714];
        if (BAO_VENUES.includes(this.$store.state.venue?.venue?.id)) {
            setTimeout(() => {
                const firstCategoryElement =
                    this.$el.querySelector('.scrollactive-item');
                if (firstCategoryElement) {
                    this.moveSliderElement(firstCategoryElement);
                    firstCategoryElement.classList.add('sticky-item-active');
                }
            }, 600);
        }
    },
    methods: {
        ...mapActions({
            setActiveFilterTags: 'helpers/setActiveFilterTags'
        }),
        moveSliderElement(target) {
            if (!target) {
                return;
            }

            let targetElement = target;

            if (this.hasCategoryHeaderImages) {
                const container = targetElement.closest('a,button');
                if (container) {
                    const labelSpan = container.querySelector('span');
                    if (labelSpan) {
                        targetElement = labelSpan;
                    } else {
                        targetElement = container;
                    }
                }
            }

            // Batch DOM reads first to avoid layout thrashing
            requestAnimationFrame(() => {
                const width = targetElement.offsetWidth;
                const left = targetElement.offsetLeft;
                const top = targetElement.offsetTop;
                const height = targetElement.offsetHeight;

                // Then batch all writes
                this.isSliderElementVisible = true;
                this.sliderElementProperties = {
                    width: width + 5 + 'px',
                    left: left - 2.5 + 'px',
                    top: top - 2.5 + 'px',
                    height: height + 5 + 'px'
                };
            });
        },
        onItemChanged(event, currentItem, lastActiveItem) {
            if (!currentItem || !currentItem.parentNode) {
                this.isSliderElementVisible = false;

                return;
            }

            if (event) {
                this.moveSliderElement(currentItem);
            }

            const currentItemPos =
                currentItem.getBoundingClientRect().left + window.scrollX;

            let lastItemPos = 0;
            if (lastActiveItem) {
                lastItemPos =
                    lastActiveItem.getBoundingClientRect().left +
                    window.scrollX;
            }

            easyScroll({
                scrollableDomEle: currentItem.parentNode,
                direction: currentItemPos > lastItemPos ? 'right' : 'left',
                duration: 300,
                easingPreset: 'easeOutQuad',
                scrollAmount:
                    currentItemPos > lastItemPos
                        ? currentItemPos - this.offset
                        : this.offset - currentItemPos
            });
        },
        focusOnSearchInput() {
            this.$refs.searchInput.focus();
            this.isSearchExpandedKey++;
            this.searchListener();
        },
        onClickOutside() {
            if (!this.$refs.searchInput) {
                return;
            }

            this.$refs.searchInput.blur();

            if (!this.searchExpandedLock) {
                this.isSearchExpandedKey++;
            }

            if (!this.isSearchExpanded) {
                this.setActiveFilterTags(null);
            }
        },
        cancelSearch() {
            this.search = '';
            this.$refs.searchInput.blur();
            this.isSearchExpandedKey++;
            this.setActiveFilterTags(null);
        },
        scrollToElement(className) {
            const element = document.getElementsByClassName(className);

            if (element && element[0]) {
                setTimeout(() => {
                    easyScroll({
                        scrollableDomEle: window,
                        direction: 'top',
                        duration: 300,
                        easingPreset: 'easeOutQuad',
                        scrollAmount: Math.abs(
                            window.scrollY - element[0].offsetTop - 25
                        )
                    });
                }, 100);
            }
        },
        searchListener() {
            if (this.isSearchEnabled) {
                const searchInput = document.getElementById('searchInput');

                if (searchInput) {
                    const search = ({ key }) => {
                        if (key === 'Enter') {
                            this.$refs.searchInput.blur();
                            searchInput.removeEventListener('keydown', search);
                        }
                    };

                    searchInput.addEventListener('keydown', search);
                }
            }
        },
        openAllergenFiltersModal() {
            this.$modal.show('allergen-filters-modal');
        },
        isActiveCategory(category) {
            return (
                this.hasCategoryRouting && this.activeCategoryId === category.id
            );
        },
        onCategoryNavClick(category) {
            if (!this.hasCategoryRouting) return;
            if (this.activeCategoryId === category.id) return;

            this.$router.push({
                name: 'menuCategory',
                params: {
                    venueSlug: this.$route.params.venueSlug,
                    categoryId: category.id
                },
                query: this.$route.query
            });
        },
        updateSliderForActiveCategory() {
            // No active category (e.g. on /menu) or routing mode off → hide slider
            if (!this.hasCategoryRouting || this.activeCategoryId == null) {
                this.isSliderElementVisible = false;
                return;
            }

            // We do have an active category → position slider
            this.$nextTick(() => {
                const activeEl = this.$el.querySelector(
                    '.venue-category-menu__list .sticky-item-active'
                );
                if (activeEl) {
                    this.moveSliderElement(activeEl);
                }
            });
        }
    },
    computed: {
        ...mapGetters({
            activeFilterTags: 'helpers/activeFilterTags',
            searchExpandedLock: 'helpers/searchExpandedLock',
            selectedArea: 'venue/selectedArea',
            allergenFilters: 'helpers/allergenFilters',
            dietFilters: 'helpers/dietFilters',
            venueValue: 'venue/venueValue'
        }),
        ...mapState('venue', {
            visualMenu: state => state.venue.visualMenu,
            isLoading: state => state.isLoading
        }),
        ...mapState('menu', {
            categories: state => state.categories
        }),
        ...mapState('cart', {
            fulfillmentMethod: state => state.fulfillmentMethod
        }),
        isMobile() {
            return isMobile();
        },
        offset() {
            const windowWidth = window.innerWidth;
            return isMobile() ? 12 : windowWidth * 0.2;
        },
        isSearchExpanded() {
            this.isSearchExpandedKey;
            const isFocused = document.activeElement.id === 'searchInput';

            return (
                this.search || isFocused || this.activeFilterTags?.length > 0
            );
        },
        isSearchEnabled() {
            let productsLength = 0;

            this.categories.forEach(category => {
                productsLength += category.products.length;
            });

            return productsLength > 20;
        },
        hasDietaryFilters() {
            return (
                this.$growthbook.isOn('dietary_filters') ||
                this.$store.state.venue?.venue?.id === 9469 ||
                FulfillmentMethodsEnum.isCateringMethod(this.fulfillmentMethod)
            );
        },
        navCategories() {
            return menuService.filterVisibleCategories(
                this.categories,
                this.selectedArea,
                {
                    excludeHidden: true,
                    parentOnly: this.$growthbook.isOn('sub_categories')
                }
            );
        },
        isBao() {
            return (
                this.$store.state.venue?.venue?.accountId ===
                '5f4e9ddc-3431-43f5-9de0-7aa356564afc'
            );
        },
        categoryNavImagesSetting() {
            return this.$store.state.venue?.venue?.categoryNavImages;
        },
        hasCategoryHeaderImages() {
            if (!this.categoryNavImagesSetting) {
                return false;
            }
            return (
                this.categoryNavImagesSetting ===
                    CategoryNavImagesEnum.DESKTOP_MOBILE ||
                (this.categoryNavImagesSetting ===
                    CategoryNavImagesEnum.DESKTOP &&
                    !this.isMobile)
            );
        },
        computedOffset() {
            const baseOffset = 80;
            const imageOffset = 160;
            return this.hasCategoryHeaderImages ? imageOffset : baseOffset;
        },
        hasAllergenFilters() {
            return this.$growthbook.isOn('allergen_filters');
        },
        hasCategoryRouting() {
            return this.venueValue('menuView') === CategoryRoutingEnum.PAGES;
        },
        activeCategoryId() {
            if (this.$route.name !== 'menuCategory') return null;
            const id = this.$route.params.categoryId;
            return id ? parseInt(id, 10) : null;
        }
    },

    watch: {
        search(value) {
            this.$emit('search', value);
            this.scrollToElement('menu-page__menu-category');
        },
        activeCategoryId: {
            handler() {
                this.updateSliderForActiveCategory();
            },
            immediate: true
        }
    }
};
</script>

<style scoped src="@/assets/css/menu-sticky-header_scoped.css"></style>
